import { PRINT_ADS, DIGITAL_ADS } from '../config';
import { calcPricing, getRunSchedule, getPrintAdPrice, toDateInputValue } from './pricing';

// The order as the staff email and the Bookings Sheet need it. Built from the
// form state alone, so the browser and the order-notify function produce the
// exact same order from the exact same selections: the server rebuilds it from
// `order` (see serializeOrder) instead of trusting the prices the browser sent.
// `extra` carries what differs per send (PayPal id, kind, browser).
export function buildOrderPayload(form, extra = {}) {
  const pricing         = calcPricing(form);
  const runScheduleKeys = getRunSchedule(form.startDate, form.weeks).map(toDateInputValue);

  const hasPrint    = form.medium === 'print' || form.medium === 'both';
  const hasDigital  = form.medium === 'digital' || form.medium === 'both';
  const isBundle    = form.medium === 'both';
  const isNonprofit = form.orgType === 'nonprofit';
  const printAd     = hasPrint && form.printAd ? PRINT_ADS.find(a => a.id === form.printAd) : null;
  const printPrice  = printAd ? getPrintAdPrice(printAd, isNonprofit) : 0;

  const hasOrientations = printAd && printAd.specs.length > 1;
  const spec = printAd
    ? (hasOrientations
        ? (printAd.specs.find(s => s.orientation === form.orientation) || printAd.specs[0])
        : printAd.specs[0])
    : null;

  const contact = form.contact || {};

  return {
    business:     contact.businessName,
    contactName:  contact.name,
    email:        contact.email,
    phone:        contact.phone,
    orgType:      form.orgType,
    // A typed EIN survives org-type toggling in form state; only
    // attach it to the order when the rate actually depends on it.
    ein:          isNonprofit ? (form.ein || '') : '',
    medium:       form.medium,
    printAd: printAd ? {
      label:          printAd.label,
      manifestLabel:  spec.manifestLabel,
      orientation:    spec.label || null,
      dims:           spec.dims,
      format:         printAd.format,
      pageEquivalent: printAd.pageEquivalent,
      // The rate actually charged (non-profit or business) — the
      // Bookings-sheet manifest builds its cost column from this.
      price:          printPrice,
    } : null,
    // Matches calcPricing: digital placements only count on a digital order.
    digitalAds: (hasDigital ? (form.digitalAds || []) : [])
      .map(id => DIGITAL_ADS.find(a => a.id === id))
      .filter(Boolean)
      .map(ad => {
        const unit = isBundle
          ? (isNonprofit ? ad.nonprofitAddonPrice : ad.addonPrice)
          : (isNonprofit ? ad.nonprofitStandalonePrice : ad.standalonePrice);
        const skips      = (form.digitalSkips && form.digitalSkips[ad.id]) || [];
        const adRunDates = runScheduleKeys.filter(d => !skips.includes(d));
        return {
          id:            ad.id,
          label:         ad.label,
          manifestLabel: ad.manifestLabel,
          dimensions:    ad.dimensions,
          price:         unit,
          weeks:         adRunDates.length,
          runDates:      adRunDates,
        };
      }),
    weeks:       form.weeks,
    runSchedule: runScheduleKeys,
    // Only report the code when it actually moved the price, so the
    // order email never claims a sale that didn't discount anything.
    promo: pricing.promoApplies ? {
      code:            form.promo,
      label:           pricing.promo.label,
      discountPerWeek: pricing.promoPerWeek,
      savings:         pricing.promoSavings,
    } : null,
    pricing,
    // The raw selections, so the server can rebuild this whole payload itself.
    order: serializeOrder(form),
    ...extra,
  };
}

// Form state -> plain JSON the server can rebuild the order from. Only the
// fields that decide what runs and what it costs, plus the contact details.
export function serializeOrder(form) {
  return {
    orgType:      form.orgType,
    ein:          form.ein || '',
    medium:       form.medium,
    printAd:      form.printAd || null,
    orientation:  form.orientation || null,
    digitalAds:   form.digitalAds || [],
    digitalSkips: form.digitalSkips || {},
    weeks:        form.weeks,
    startDate:    form.startDate ? toDateInputValue(form.startDate) : null,
    promo:        form.promo || null,
    contact: {
      name:         (form.contact && form.contact.name) || '',
      businessName: (form.contact && form.contact.businessName) || '',
      email:        (form.contact && form.contact.email) || '',
      phone:        (form.contact && form.contact.phone) || '',
    },
  };
}

// The inverse of serializeOrder. Returns null when the selections are missing
// or malformed, so the caller can refuse to auto-book rather than guess.
export function deserializeOrder(order) {
  if (!order || typeof order !== 'object') return null;
  const m = String(order.startDate || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const weeks = Number(order.weeks);
  if (!m || !Number.isInteger(weeks) || weeks < 1 || weeks > 52) return null;
  if (!['print', 'digital', 'both'].includes(order.medium)) return null;
  const startDate = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // Runs start on Wednesdays only.
  if (startDate.getDay() !== 3) return null;
  return {
    orgType:      order.orgType === 'nonprofit' ? 'nonprofit' : 'business',
    ein:          String(order.ein || ''),
    medium:       order.medium,
    printAd:      order.printAd || null,
    orientation:  order.orientation || null,
    digitalAds:   Array.isArray(order.digitalAds) ? order.digitalAds.map(String) : [],
    digitalSkips: (order.digitalSkips && typeof order.digitalSkips === 'object') ? order.digitalSkips : {},
    weeks,
    startDate,
    promo:        order.promo || null,
    contact:      order.contact || {},
  };
}
